import {
  AppAvatar,
  AppBox,
  AppDateAndTime,
  AppIconButton,
  AppLink,
  AppPaper,
  AppPill,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import IconDismiss from "../../../../../commons/components/icons/dismiss";
import IconProhibited from "../../../../../commons/components/icons/prohibited";
import { TeamStatusEnum } from "../../../../../commons/enums/team-status-enum";
import { IFootballTeam } from "../../../../../commons/models/football/interface/i-football-team";
import ability, { Can } from "../../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import { useAppSelector } from "../../../../../hooks/app";
import { ManageFootballTeam } from "../../../../../commons/models/football/admin/manage-football-team";
import { ManageFootballCompetition } from "../../../../../commons/models/football/admin/manage-football-competition";
import { IFootballCompetitionTeam } from "../../../../../commons/models/football/interface/i-football-competition-team";
import isNullOrUndefined from "../../../../../commons/utilities/null-or-undefined";
import { IFootballHostedCompetition } from "../../../../../commons/models/football/interface/i-football-competition";

interface TournamentTeamCardProps {
  competitionTeam: IFootballCompetitionTeam;
  withOutRemove?: boolean;
  withBorder?: boolean;
  onTeamRemove?: (team: IFootballTeam) => void;
}

export default function TournamentSeasonTeamCard({
  competitionTeam,
  withOutRemove,
  withBorder,
  onTeamRemove,
}: TournamentTeamCardProps) {
  const teamPath = !isNullOrUndefined(competitionTeam?.team?.parentId!)
    ? `/manage/teams/${competitionTeam?.team?.parentId!}/sub-teams/${competitionTeam.team?.id}`
    : `/manage/teams/${competitionTeam?.team?.id}`;
  const { fetchedCompetition } = useAppSelector((state) => state.fetchedCompetition);
  const canReadTeamDetails = ability.can(
    RolePolicyActionEnum.READ,
    new ManageFootballTeam().create(competitionTeam.team) as ManageFootballTeam
  );
  const handleClick = (e: MouseEvent) => {
    if (!canReadTeamDetails) {
      e.stopPropagation();
      e.preventDefault();
    }
  };
  return (
    <AppPaper padding="xs" shadow={withBorder ? "none" : "xs"} withBorder={withBorder}>
      <AppBox displayBlock flexDirection="column" gap="2xs">
        <AppBox justifyContent="space-between" alignItems="center" gap="sm">
          <AppLink
            onClick={handleClick}
            to={teamPath}
            className="flex-1"
            style={{ pointerEvents: canReadTeamDetails ? "auto" : "none" }}
          >
            <AppBox gap="xs" alignItems="center">
              <AppAvatar username={competitionTeam.team.name} src={competitionTeam.team.logo || ""} size="md" />
              <AppBox flexDirection="column">
                <AppTitle as="h6">{competitionTeam.team.shortName}</AppTitle>
                {competitionTeam.team?.founded && (
                  <AppBox gap="3xs" alignItems="center">
                    <AppText as="span" fontWeight="semibold" size="sm" color="muted">
                      Foundation Date:
                    </AppText>
                    <AppDateAndTime
                      date={competitionTeam.team?.founded || ""}
                      format="DD MMM, YYYY"
                      size="sm"
                      color="muted"
                    />
                  </AppBox>
                )}
              </AppBox>
            </AppBox>
          </AppLink>
          {competitionTeam.team?.status === Number(TeamStatusEnum.Terminated) && (
            <AppPill
              icon={<IconProhibited width={18} height={18} color="var(--fg-outline-danger)" />}
              label="Terminated"
              varient="danger"
              size="md"
            />
          )}
          {(ability.can(
            RolePolicyActionEnum.UPDATE,
            new ManageFootballTeam((fetchedCompetition as IFootballHostedCompetition).hostTeam)
          ) ||
            ability.can(RolePolicyActionEnum.UPDATE, new ManageFootballCompetition(fetchedCompetition))) &&
            !withOutRemove && (
              <AppIconButton
                variant="light"
                color="danger"
                onClick={() => {
                  if (onTeamRemove) {
                    onTeamRemove(competitionTeam.team);
                  }
                }}
                icon={<IconDismiss />}
              />
            )}
        </AppBox>
      </AppBox>
    </AppPaper>
  );
}
